/**
 * TaxonomyPage
 *
 * Main page for taxonomy management. Switches between two modes:
 * - Grid: shows all taxonomy key cards
 * - Detail: shows a specific taxonomy's version management view
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ErrorBoundary } from '../../../ui/error-boundary';
import { Button } from '../../../ui/button';
import { useTaxonomyKeys } from '../hooks/useTaxonomy';
import GOLabRegistryNav from '../shared/GOLabRegistryNav';
import TaxonomyKeyCard from './components/TaxonomyKeyCard';
import TaxonomyDetailView from './components/TaxonomyDetailView';
import CreateTaxonomyModal from './components/CreateTaxonomyModal';
import TimelineDrawer from './components/TimelineDrawer';

const TaxonomyPage: React.FC = () => {
  const { taxonomyKey } = useParams<{ taxonomyKey?: string }>();
  const navigate = useNavigate();
  const [createNewOpen, setCreateNewOpen] = useState(false);
  const [timelineOpen, setTimelineOpen] = useState(false);

  const { data: keys, isLoading, isError, refetch } = useTaxonomyKeys();

  const pageHeader = (
    <div className="space-y-4">
      <div className="space-y-2">
        <h1 className="font-jakarta text-3xl font-extrabold tracking-[-0.04em] text-foreground md:text-4xl">
          Intelligence
        </h1>
        <p className="max-w-3xl text-[13px] leading-[1.7] text-muted-foreground">
          Manage the taxonomy and prompt registries that power classification and evaluation.
        </p>
      </div>
      <GOLabRegistryNav />
    </div>
  );

  // Detail mode
  if (taxonomyKey) {
    const summary = keys?.find((k) => k.taxonomyKey === taxonomyKey);
    return (
      <div className="flex h-full min-h-0 flex-col bg-[var(--go-spring)] px-4 py-4 md:px-6 md:py-6 xl:px-8 xl:py-6">
        <div className="flex min-h-0 flex-1 flex-col gap-6">
          {pageHeader}
          <div className="min-h-0 flex-1">
            <TaxonomyDetailView
              taxonomyKey={taxonomyKey}
              displayName={summary?.displayName || taxonomyKey}
              onBack={() => navigate('/v4/go-lab/intelligence/taxonomy')}
            />
          </div>
        </div>
      </div>
    );
  }

  // Grid mode
  if (isLoading) {
    return (
      <div className="flex h-full min-h-0 flex-col overflow-auto bg-[var(--go-spring)] px-4 py-4 md:px-6 md:py-6 xl:px-8 xl:py-6">
        <div className="space-y-6">
          {pageHeader}
          <div className="h-8 w-56 animate-pulse rounded bg-muted" />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-lg border border-border/70 bg-card" />
          ))}
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-full min-h-0 flex-col overflow-auto bg-[var(--go-spring)] px-4 py-4 md:px-6 md:py-6 xl:px-8 xl:py-6">
        <div className="space-y-6">
          {pageHeader}
          <div className="flex h-[50vh] flex-col items-center justify-center text-center">
            <p className="mb-4 text-muted-foreground">Failed to load taxonomies.</p>
            <Button variant="link" size="sm" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-auto bg-[var(--go-spring)] px-4 py-4 md:px-6 md:py-6 xl:px-8 xl:py-6">
      <div className="space-y-6">
        {pageHeader}

        <section className="space-y-5 rounded-2xl border border-border/70 bg-card p-6 shadow-[var(--shadow-card)]">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 className="font-jakarta text-2xl font-extrabold tracking-[-0.03em] text-foreground">
                Taxonomy Registry
              </h2>
              <p className="mt-1 text-[13px] leading-[1.7] text-muted-foreground">
                Manage taxonomy keys, versions, and release history.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="secondary"
                size="sm"
                className="h-8 rounded-md border-input bg-transparent px-3 text-sm font-normal text-foreground shadow-xs hover:bg-accent"
                onClick={() => setCreateNewOpen(true)}
              >
                Add New Taxonomy
              </Button>
              <Button
                variant="secondary"
                size="sm"
                className="h-8 rounded-md border-input bg-transparent px-3 text-sm font-normal text-foreground shadow-xs hover:bg-accent"
                onClick={() => setTimelineOpen(true)}
              >
                View Timeline
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {keys?.map((summary) => (
              <TaxonomyKeyCard
                key={summary.taxonomyKey}
                summary={summary}
                onClick={() => navigate(`/v4/go-lab/intelligence/taxonomy/${summary.taxonomyKey}`)}
              />
            ))}
            {(!keys || keys.length === 0) && (
              <p className="col-span-full py-8 text-center text-muted-foreground">
                No taxonomies found. Run the seed script to create initial taxonomies.
              </p>
            )}
          </div>
        </section>

        <CreateTaxonomyModal
          open={createNewOpen}
          onOpenChange={setCreateNewOpen}
        />
        {keys?.[0]?.taxonomyKey && (
          <TimelineDrawer
            taxonomyKey={keys[0].taxonomyKey}
            open={timelineOpen}
            onOpenChange={setTimelineOpen}
          />
        )}
      </div>
    </div>
  );
};

const TaxonomyPageWithBoundary: React.FC = () => (
  <ErrorBoundary>
    <TaxonomyPage />
  </ErrorBoundary>
);

export default TaxonomyPageWithBoundary;
